import { CalendarDays, ChartColumn, Sigma, TrendingUp, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useActiveGroup } from '@/features/groups/store';
import { useLedgerStore } from '@/features/ledger/store';
import { BarList, ColumnChart, GroupedBars, Legend } from '@/features/reports/charts';
import {
  reportByCategory,
  reportByMember,
  reportByMonth,
  type CategoryReportRow,
  type MemberReportRow,
  type MonthReportRow,
  type ReportRange,
} from '@/features/reports/repository';
import {
  formatMonthKey,
  formatMonthShort,
  monthsAgoISO,
  startOfMonthISO,
  startOfYearISO,
  todayISO,
} from '@/lib/date';
import {
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  LoadingState,
  Money,
  PageHeader,
  SegmentedControl,
  StatTile,
  useSeriesColors,
  type Column,
} from '@/ui';
import { formatMoney } from '@domain/money';

type ReportResult = {
  /** Identifies the query these rows answer; see `requestKey` below. */
  key: string;
  byCategory: CategoryReportRow[];
  byMember: MemberReportRow[];
  byMonth: MonthReportRow[];
};

type RangeKey = 'month' | 'quarter' | 'year' | 'all';
type ReportTab = 'category' | 'member' | 'month';
type ViewMode = 'chart' | 'table';

const RANGE_LABELS: Record<RangeKey, string> = {
  month: 'This month',
  quarter: 'Last 3 months',
  year: 'This year',
  all: 'All time',
};

function resolveRange(key: RangeKey): ReportRange {
  switch (key) {
    case 'month':
      return { from: startOfMonthISO(), to: todayISO() };
    case 'quarter':
      return { from: monthsAgoISO(2), to: todayISO() };
    case 'year':
      return { from: startOfYearISO(), to: todayISO() };
    case 'all':
      return {};
  }
}

export function ReportsPage() {
  const navigate = useNavigate();
  const group = useActiveGroup();
  const allExpenses = useLedgerStore((state) => state.allExpenses);
  const series = useSeriesColors();

  const [rangeKey, setRangeKey] = useState<RangeKey>('all');
  const [tab, setTab] = useState<ReportTab>('category');
  const [view, setView] = useState<ViewMode>('chart');
  const [result, setResult] = useState<ReportResult | null>(null);

  const currency = group?.currency ?? 'USD';
  const groupId = group?.id ?? null;
  const range = useMemo(() => resolveRange(rangeKey), [rangeKey]);

  /**
   * One key describing the query currently on screen. `allExpenses` is part of
   * it so an edit made on another page re-runs the aggregates.
   */
  const requestKey = `${groupId ?? ''}|${rangeKey}|${allExpenses.length}|${
    allExpenses[0]?.updatedAt ?? ''
  }`;

  // `loading` is derived rather than stored, so nothing sets state during the
  // effect body — only in the async continuation, once the rows are back.
  const loading = result?.key !== requestKey;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!groupId) {
        if (!cancelled) {
          setResult({ key: requestKey, byCategory: [], byMember: [], byMonth: [] });
        }
        return;
      }

      const [byCategory, byMember, byMonth] = await Promise.all([
        reportByCategory(groupId, range),
        reportByMember(groupId, range),
        reportByMonth(groupId, range),
      ]);

      if (!cancelled) setResult({ key: requestKey, byCategory, byMember, byMonth });
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, range, requestKey]);

  const byCategory = result?.byCategory ?? [];
  const byMember = result?.byMember ?? [];
  const byMonth = result?.byMonth ?? [];

  const totalMinor = byCategory.reduce((total, row) => total + row.totalMinor, 0);
  const expenseCount = byCategory.reduce((total, row) => total + row.expenseCount, 0);
  const averageMinor = expenseCount > 0 ? Math.round(totalMinor / expenseCount) : 0;
  const busiestMonth = [...byMonth].sort((a, b) => b.totalMinor - a.totalMinor)[0] ?? null;

  if (!group) {
    return (
      <>
        <PageHeader title="Reports" />
        <EmptyState
          icon={ChartColumn}
          title="No group selected"
          description="Choose a group to see its reports."
          action={<Button onClick={() => navigate('/groups')}>Choose a group</Button>}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Reports"
        eyebrow={group.name}
        description={RANGE_LABELS[rangeKey]}
        actions={
          <SegmentedControl
            value={view}
            onChange={setView}
            segments={[
              { value: 'chart', label: 'Chart' },
              { value: 'table', label: 'Table' },
            ]}
          />
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SegmentedControl
          value={rangeKey}
          onChange={setRangeKey}
          segments={(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => ({
            value: key,
            label: RANGE_LABELS[key],
          }))}
        />
        <SegmentedControl
          value={tab}
          onChange={setTab}
          segments={[
            { value: 'category', label: 'By category' },
            { value: 'member', label: 'By member' },
            { value: 'month', label: 'Over time' },
          ]}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatTile
          className="col-span-2 sm:col-span-1"
          label="Total spent"
          icon={Wallet}
          value={formatMoney(totalMinor, currency)}
          caption={`${expenseCount} expense${expenseCount === 1 ? '' : 's'}`}
        />
        <StatTile
          label="Average expense"
          icon={Sigma}
          value={formatMoney(averageMinor, currency)}
          caption={RANGE_LABELS[rangeKey]}
        />
        <StatTile
          label="Busiest month"
          icon={CalendarDays}
          value={busiestMonth ? formatMonthKey(busiestMonth.monthKey) : '—'}
          caption={busiestMonth ? formatMoney(busiestMonth.totalMinor, currency) : undefined}
        />
      </div>

      <div className="mt-6">
        {loading ? (
          <Card padded={false}>
            <LoadingState label="Crunching numbers…" />
          </Card>
        ) : allExpenses.length === 0 ? (
          <Card padded={false}>
            <EmptyState
              icon={ChartColumn}
              title="Nothing to report yet"
              description="Record a few expenses and the breakdowns will appear here."
            />
          </Card>
        ) : expenseCount === 0 ? (
          <Card padded={false}>
            <EmptyState
              icon={TrendingUp}
              title="No expenses in this period"
              description="Try a wider date range."
              action={<Button onClick={() => setRangeKey('all')}>Show all time</Button>}
            />
          </Card>
        ) : tab === 'category' ? (
          <CategoryReport rows={byCategory} currency={currency} view={view} total={totalMinor} />
        ) : tab === 'member' ? (
          <MemberReport
            rows={byMember}
            currency={currency}
            view={view}
            colors={series}
            onOpen={(memberId) => navigate(`/members/${memberId}`)}
          />
        ) : (
          <MonthReport rows={byMonth} currency={currency} view={view} color={series.primary} />
        )}
      </div>
    </>
  );
}

function CategoryReport({
  rows,
  currency,
  view,
  total,
}: {
  rows: CategoryReportRow[];
  currency: string;
  view: ViewMode;
  total: number;
}) {
  const max = Math.max(...rows.map((row) => row.totalMinor), 1);

  const columns: Column<CategoryReportRow>[] = [
    {
      key: 'name',
      header: 'Category',
      cell: (row) => (
        <span className="flex items-center gap-2">
          <span
            className="size-2.5 rounded-full"
            style={{ backgroundColor: row.color }}
            aria-hidden
          />
          {row.name}
        </span>
      ),
    },
    {
      key: 'count',
      header: 'Expenses',
      align: 'right',
      cell: (row) => <span className="tnum text-muted">{row.expenseCount}</span>,
    },
    {
      key: 'share',
      header: 'Share of spend',
      align: 'right',
      cell: (row) => (
        <span className="tnum text-muted">
          {total > 0 ? Math.round((row.totalMinor / total) * 100) : 0}%
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      width: '150px',
      cell: (row) => (
        <Money minor={row.totalMinor} currency={currency} className="font-semibold" />
      ),
    },
  ];

  if (view === 'table') {
    return <DataTable columns={columns} rows={rows} rowKey={(row) => row.categoryId ?? 'none'} />;
  }

  return (
    <Card>
      <CardHeader
        title="Spend by category"
        description="Bar length is relative to the largest category. Each bar carries its own name and total, so the colours are identity, not information."
      />
      <BarList
        data={rows.map((row) => ({
          key: row.categoryId ?? 'uncategorised',
          label: row.name,
          value: formatMoney(row.totalMinor, currency),
          fraction: row.totalMinor / max,
          color: row.color,
          caption: `${row.expenseCount} expense${row.expenseCount === 1 ? '' : 's'} · ${
            total > 0 ? Math.round((row.totalMinor / total) * 100) : 0
          }% of spend`,
          tooltip: (
            <span>
              {row.name} <b className="tnum">{formatMoney(row.totalMinor, currency)}</b>
            </span>
          ),
        }))}
      />
    </Card>
  );
}

function MemberReport({
  rows,
  currency,
  view,
  colors,
  onOpen,
}: {
  rows: MemberReportRow[];
  currency: string;
  view: ViewMode;
  colors: { primary: string; secondary: string };
  onOpen: (memberId: string) => void;
}) {
  const max = Math.max(
    ...rows.map((row) => Math.max(row.paidMinor, row.shareMinor)),
    1
  );

  const columns: Column<MemberReportRow>[] = [
    { key: 'name', header: 'Member', cell: (row) => row.name },
    {
      key: 'count',
      header: 'Expenses shared',
      align: 'right',
      cell: (row) => <span className="tnum text-muted">{row.expenseCount}</span>,
    },
    {
      key: 'paid',
      header: 'Paid',
      align: 'right',
      cell: (row) => <Money minor={row.paidMinor} currency={currency} />,
    },
    {
      key: 'share',
      header: 'Their share',
      align: 'right',
      cell: (row) => <Money minor={row.shareMinor} currency={currency} />,
    },
    {
      key: 'gap',
      header: 'Difference',
      align: 'right',
      width: '140px',
      cell: (row) => (
        <Money
          minor={row.paidMinor - row.shareMinor}
          currency={currency}
          signed
          className="font-semibold"
        />
      ),
    },
  ];

  if (view === 'table') {
    return (
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.memberId}
        onRowClick={(row) => onOpen(row.memberId)}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        title="Paid vs. share"
        description="Both bars use one shared scale. The gap between a member's two bars is their balance."
        action={
          <Legend
            items={[
              { name: 'Paid', color: colors.primary },
              { name: 'Their share', color: colors.secondary },
            ]}
          />
        }
      />
      <GroupedBars
        max={max}
        data={rows.map((row) => ({
          key: row.memberId,
          label: row.name,
          caption: `${row.expenseCount} shared`,
          onClick: () => onOpen(row.memberId),
          series: [
            {
              name: 'Paid',
              value: row.paidMinor,
              display: formatMoney(row.paidMinor, currency),
              color: colors.primary,
            },
            {
              name: 'Their share',
              value: row.shareMinor,
              display: formatMoney(row.shareMinor, currency),
              color: colors.secondary,
            },
          ],
        }))}
      />
    </Card>
  );
}

function MonthReport({
  rows,
  currency,
  view,
  color,
}: {
  rows: MonthReportRow[];
  currency: string;
  view: ViewMode;
  color: string;
}) {
  const columns: Column<MonthReportRow>[] = [
    { key: 'month', header: 'Month', cell: (row) => formatMonthKey(row.monthKey) },
    {
      key: 'count',
      header: 'Expenses',
      align: 'right',
      cell: (row) => <span className="tnum text-muted">{row.expenseCount}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      width: '150px',
      cell: (row) => (
        <Money minor={row.totalMinor} currency={currency} className="font-semibold" />
      ),
    },
  ];

  if (view === 'table') {
    return (
      <DataTable
        columns={columns}
        rows={[...rows].reverse()}
        rowKey={(row) => row.monthKey}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        title="Spend over time"
        description="Group total per calendar month, oldest first."
      />
      <ColumnChart
        color={color}
        data={rows.map((row) => ({
          key: row.monthKey,
          label: formatMonthShort(row.monthKey),
          value: row.totalMinor,
          display: formatMoney(row.totalMinor, currency),
          caption: `${row.expenseCount} expense${row.expenseCount === 1 ? '' : 's'}`,
        }))}
      />
    </Card>
  );
}
